/**
 * About Us — the team, in the old page's order, and Professor Ka in the last
 * seat. Plain data (no React), so the page and the HTML crawlers read
 * (`seo/page-content`) list the same people.
 */
import type { BadgeHue } from './landing-hues';

export type Member = {
  name: string;
  /** Titles stay in English, as on the old page. */
  role: string;
  /** `homeV2.aboutPage.<bio>` — its whole key is `BIO_KEY[bio]`. */
  bio: keyof typeof BIO_KEY;
  /** Its role badge colourway and hover light. */
  hue: BadgeHue;
  /** A portrait in `public/assets/`; absent for Ka, who sits at his desk. */
  photo?: string;
};

/** Each bio's whole i18n key (greppable). */
export const BIO_KEY = {
  stefanDescription: 'homeV2.aboutPage.stefanDescription',
  davidDescription: 'homeV2.aboutPage.davidDescription',
  simoneDescription: 'homeV2.aboutPage.simoneDescription',
  darioDescription: 'homeV2.aboutPage.darioDescription',
  saraDescription: 'homeV2.aboutPage.saraDescription',
  massimoDescription: 'homeV2.aboutPage.massimoDescription',
  jakubDescription: 'homeV2.aboutPage.jakubDescription',
  kaDescription: 'homeV2.aboutPage.kaDescription',
} as const;

/** The team in the old page's order — and Professor Ka in the last seat. */
export const TEAM: Member[] = [
  {
    name: 'Stefan Djokovic',
    role: 'CTO & Co-Founder',
    bio: 'stefanDescription',
    photo: 'ste.jpeg',
    hue: 'primary',
  },
  {
    name: 'David Djokovic',
    role: 'CEO & Co-Founder',
    bio: 'davidDescription',
    photo: 'david.jpeg',
    hue: 'primary',
  },
  {
    name: 'Simone De Marchi',
    role: 'CPO & Co-Founder',
    bio: 'simoneDescription',
    photo: 'simo.png',
    hue: 'primary',
  },
  {
    name: 'Dario Passarello',
    role: 'Software Engineer',
    bio: 'darioDescription',
    photo: 'dario.jpeg',
    hue: 'sky',
  },
  {
    name: 'Sara De Villa',
    role: 'Growth marketer',
    bio: 'saraDescription',
    photo: 'sara.png',
    hue: 'pink',
  },
  {
    name: 'Massimo Parisi',
    role: 'Founding engineer',
    bio: 'massimoDescription',
    photo: 'massimo.png',
    hue: 'sky',
  },
  {
    name: 'Jakub Pietrzak',
    role: 'Software Engineer',
    bio: 'jakubDescription',
    photo: 'jakub.png',
    hue: 'sky',
  },
  {
    name: 'Professor Ka',
    role: 'AI Tutor',
    bio: 'kaDescription',
    hue: 'amber',
  },
];

/** The six values, in page order; their copy is `ABOUT_VALUE_COPY`. */
export const ABOUT_VALUE_KEYS = [
  'accessible',
  'effective',
  'personalised',
  'fun',
  'social',
  'pleasant',
] as const;

/** Each value's whole copy keys, shared by the page and the crawler copy. */
export const ABOUT_VALUE_COPY = {
  accessible: {
    titleKey: 'homeV2.aboutPage.accessible',
    descriptionKey: 'homeV2.aboutPage.accessibleDescription',
  },
  effective: {
    titleKey: 'homeV2.aboutPage.effective',
    descriptionKey: 'homeV2.aboutPage.effectiveDescription',
  },
  personalised: {
    titleKey: 'homeV2.aboutPage.personalised',
    descriptionKey: 'homeV2.aboutPage.personalisedDescription',
  },
  fun: {
    titleKey: 'homeV2.aboutPage.fun',
    descriptionKey: 'homeV2.aboutPage.funDescription',
  },
  social: {
    titleKey: 'homeV2.aboutPage.social',
    descriptionKey: 'homeV2.aboutPage.socialDescription',
  },
  pleasant: {
    titleKey: 'homeV2.aboutPage.pleasant',
    descriptionKey: 'homeV2.aboutPage.pleasantDescription',
  },
} as const satisfies Record<(typeof ABOUT_VALUE_KEYS)[number], unknown>;
